import * as React from "react";
import { useState, useEffect } from "react";
import { Formik, Form, Field, useField } from "formik";
import styled, { css } from "styled-components";
import URI from "urijs";
import { SAMLConfig } from "@graylog/enterprise-api";
import { validateField } from "util/FormsUtils";
import {
  Wizard,
  FormikFormGroup,
  InputOptionalInfo as Opt,
  Spinner,
  Select,
  MultiSelect,
  TimezoneSelect
} from "components/common";
import { Row, Col, ButtonToolbar, Button, Input, Alert } from "components/bootstrap";
import {
  AUTH_SERVICE_TYPE,
  SSO_SAML_PATH,
  SSO_SAML_ACS_PATH,
  SSO_SAML_LOGOUT_PATH
} from "authentication/components/saml/constants";
import formatAuthBackendValidationErrors from "authentication/helpers/formatAuthBackendValidationErrors";
import samlAuthBackendSubmit from "authentication/components/saml/config/helpers/samlAuthBackendSubmit";
import AuthBackendSubmitError from "authentication/components/AuthBackendSubmitError";
import Routes from "routing/Routes";
import useHistory from "routing/useHistory";
import useRoles from "common/hooks/useRoles";
import AppConfig from "util/AppConfig";
import SAMLPEMField from "authentication/components/saml/config/SAMLPEMField";
import useSAMLDefaultAttributeMapping from "authentication/components/saml/hooks/useSAMLDefaultAttributeMapping";
import SAMLCertInfoTable from "authentication/components/saml/config/SAMLCertInfoTable";
import { getPathnameWithoutId } from "util/URLUtils";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useLocation from "routing/useLocation";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
const DEFAULT_SP_BASE_URL = `${window.location.origin}${AppConfig.gl2AppPathPrefix()}`;
const normalizeURI = (url) => new URI(url).normalizePath().toString();
const StyledDefList = styled.dl(
  ({ theme }) => css`
    dd {
      margin-bottom: ${theme.spacings.sm};
    }
  `
);
const StyledSectionWrapper = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.sm};

    p {
      margin-top: ${theme.spacings.sm};
    }
  `
);
const AttributeMapping = ({ title, mappings }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("strong", null, title), /* @__PURE__ */ React.createElement("ul", null, (mappings != null ? mappings : []).sort().map((attributeName) => /* @__PURE__ */ React.createElement("li", { key: attributeName }, /* @__PURE__ */ React.createElement("code", null, attributeName)))));
const FormSection = ({ title, children = void 0 }) => /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledSectionWrapper, null, /* @__PURE__ */ React.createElement("hr", null), /* @__PURE__ */ React.createElement("h3", null, title), children)));
const BackendFetchError = ({ message, title }) => {
  if (!message) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", style: { wordBreak: "break-word" }, title }, message)));
};
const CertificateInfo = ({ certInfo, error = void 0 }) => {
  if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, error);
  }
  if (!certInfo) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    SAMLCertInfoTable,
    {
      certificate: certInfo.certificate,
      subject: certInfo.subject,
      issuer: certInfo.issuer,
      sha256Fingerprint: certInfo.sha256_fingerprint,
      sha1Fingerprint: certInfo.sha1_fingerprint,
      validFrom: certInfo.valid_from,
      validTo: certInfo.valid_to
    }
  );
};
const CertificateInfoRow = ({ certInfo, error = void 0 }) => /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(CertificateInfo, { certInfo, error })));
const GenerateKeyPairButton = ({ values, onClick }) => {
  const { spCredentialCertificate, spCredentialPrivateKey } = values;
  const isDestructive = spCredentialCertificate || (spCredentialPrivateKey == null ? void 0 : spCredentialPrivateKey.is_set) || (spCredentialPrivateKey == null ? void 0 : spCredentialPrivateKey.set_value);
  return /* @__PURE__ */ React.createElement(Button, { type: "button", bsStyle: isDestructive ? "danger" : "info", onClick }, isDestructive ? "Regenerate" : "Generate", " Certificate and Key");
};
const ValidationErrorSummary = ({ errors }) => {
  if (!errors || Object.keys(errors).length === 0) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(Alert, { title: "Validation Errors", bsStyle: "danger" }, "The form has validation errors. Please check all form fields above.")));
};
const MetadataRefreshInfo = ({ show }) => {
  if (!show) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info", style: { margin: 0 } }, "The IdP metadata will be refreshed on every login, so the following fields can't be changed.");
};
const fetchCertInfo = (cert, setValue, setError) => {
  if (cert) {
    SAMLConfig.getCertificateInfo({ certificate: cert }).then((info) => {
      setValue(info);
      setError(void 0);
    }).catch((error) => {
      var _a, _b;
      setValue(void 0);
      setError(
        ((_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.message) || "Error fetching certificate info. Please check that the file is a valid PEM file."
      );
    });
  } else {
    setValue(void 0);
    setError(void 0);
  }
};
const IdPConfigForm = ({
  values,
  roles,
  isSubmitting,
  isFetchingMetadata,
  validationErrors,
  submitErrors,
  metadataFetchErrors,
  spCredentialsGenerateErrors,
  setActiveStep,
  setFieldValue,
  handleMetadataFetch,
  handleSpCredentialFetch,
  defaultAttributeMapping
}) => {
  var _a, _b;
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  useEffect(() => {
    fetchCertInfo(
      values.idpCertificate,
      (info) => setFieldValue("idpCertificateInfo", info),
      (error) => setFieldValue("idpCertificateInfoError", error)
    );
  }, [values.idpCertificate, setFieldValue]);
  useEffect(() => {
    fetchCertInfo(
      values.spCredentialCertificate,
      (info) => setFieldValue("spCredentialCertificateInfo", info),
      (error) => setFieldValue("spCredentialCertificateInfoError", error)
    );
  }, [values.spCredentialCertificate, setFieldValue]);
  const rolesOptions = roles.map((role) => ({ label: role.name, value: role.id }));
  const { idpMetadataRefreshAtLogin: isRefreshAtLogin } = values;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 7 }, /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "title",
      label: "Title",
      placeholder: "Title",
      disabled: isSubmitting,
      validate: validateField({ required: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      type: "textarea",
      name: "description",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Description ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: "Description",
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(FormSection, { title: "Identity Provider Config" }), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpMetadataUrl",
      label: "Metadata URL",
      placeholder: "Metadata URL",
      help: "The SAML metadata URL of your IdP.",
      disabled: isSubmitting || isFetchingMetadata,
      validate: validateField({ url: true }),
      buttonAfter: /* @__PURE__ */ React.createElement(
        Button,
        {
          type: "button",
          bsStyle: values.idpMetadataUrl ? "info" : "default",
          disabled: !values.idpMetadataUrl,
          onClick: handleMetadataFetch(values, setFieldValue)
        },
        "Fetch"
      )
    }
  ), /* @__PURE__ */ React.createElement(BackendFetchError, { title: "Failed to fetch IdP metadata:", message: metadataFetchErrors }), /* @__PURE__ */ React.createElement(
    Input,
    {
      label: "Metadata Refresh At Login",
      id: "idp-metadata-refresh-at-login-checkbox",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        type: "checkbox",
        name: "idpMetadataRefreshAtLogin",
        label: "",
        help: "Select this option if you want to refresh the IdP metadata on every login.",
        wrapperClassName: "col-sm-12"
      }
    ),
    /* @__PURE__ */ React.createElement(MetadataRefreshInfo, { show: isRefreshAtLogin })
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpSsoServiceRedirectBindingUrl",
      label: "SSO Service Redirect URL",
      placeholder: "Single Sign-On Service Redirect Binding URL",
      help: "The Single Sign-On Service Redirect Binding URL of your IdP.",
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpSsoServicePostBindingUrl",
      label: "SSO Service POST URL",
      placeholder: "Single Sign-On Service POST Binding URL",
      help: "The Single Sign-On Service POST Binding URL of your IdP.",
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpLogoutServiceRedirectBindingUrl",
      label: "Logout Service Redirect URL",
      placeholder: "Single Logout Service Redirect Binding URL",
      help: "The Single Logout Service Redirect Binding URL of your IdP.",
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpLogoutServicePostBindingUrl",
      label: "Logout Service POST URL",
      placeholder: "Single Logout Service POST Binding URL",
      help: "The Single Logout Service POST Binding URL of your IdP.",
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpEntityId",
      label: "Entity ID",
      placeholder: "Entity ID",
      help: "The entity ID of your IdP.",
      disabled: isSubmitting || isFetchingMetadata || isRefreshAtLogin,
      validate: validateField({ required: true })
    }
  ), /* @__PURE__ */ React.createElement(
    SAMLPEMField,
    {
      name: "idpCertificate",
      filename: "idpCertificateFilename",
      label: "Certificate",
      help: "The signing certificate of your IdP.",
      isValueSet: !!values.idpCertificate,
      disabled: isRefreshAtLogin
    }
  ), /* @__PURE__ */ React.createElement(CertificateInfoRow, { certInfo: values.idpCertificateInfo, error: values.idpCertificateInfoError }), /* @__PURE__ */ React.createElement(FormSection, { title: "Service Provider Config" }), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "spBaseUrl",
      label: "Base URL",
      placeholder: "Base URL",
      help: /* @__PURE__ */ React.createElement("span", null, "The base URL of your setup for this configuration. The URL is used for SAML endpoints.", " ", /* @__PURE__ */ React.createElement("strong", null, "You usually don't need to change this value.")),
      disabled: isSubmitting,
      validate: validateField({ required: true, url: true })
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "spEntityId",
      label: "Entity ID",
      placeholder: "Entity ID",
      help: "The entity ID for this SP configuration.",
      disabled: isSubmitting,
      validate: validateField({ required: true })
    }
  ), /* @__PURE__ */ React.createElement(
    SAMLPEMField,
    {
      name: "spCredentialCertificate",
      filename: "spCredentialCertificateFilename",
      label: "Certificate",
      help: "The signing certificate for this SP configuration.",
      isValueSet: !!values.spCredentialCertificate
    },
    "Use the button below to generate a new key pair, drag a PEM file here or click to select a PEM file"
  ), /* @__PURE__ */ React.createElement(
    CertificateInfoRow,
    {
      certInfo: values.spCredentialCertificateInfo,
      error: values.spCredentialCertificateInfoError
    }
  ), /* @__PURE__ */ React.createElement(
    SAMLPEMField,
    {
      name: "spCredentialPrivateKey",
      filename: "spCredentialPrivateKeyFilename",
      label: "Private Key",
      help: "The signing key for this SP configuration.",
      isValueSet: ((_a = values.spCredentialPrivateKey) == null ? void 0 : _a.is_set) || ((_b = values.spCredentialPrivateKey) == null ? void 0 : _b.set_value),
      encrypted: true
    },
    "Use the button below to generate a new key pair, drag a PEM file here or click to select a PEM file"
  ), /* @__PURE__ */ React.createElement(Input, { id: "sp-generate-credential", label: " ", labelClassName: "col-sm-3", wrapperClassName: "col-sm-9" }, /* @__PURE__ */ React.createElement(GenerateKeyPairButton, { values, onClick: handleSpCredentialFetch(setFieldValue) })), /* @__PURE__ */ React.createElement(BackendFetchError, { title: "Failed to generate SP credentials:", message: spCredentialsGenerateErrors }), /* @__PURE__ */ React.createElement(FormSection, { title: "User Attribute Mapping" }, /* @__PURE__ */ React.createElement("p", null, "By default, the authentication service tries to extract commonly used user attribute names from the SAML authentication response. You can define custom attribute names if the defaults don't work.")), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpUserNameAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, "User Name ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: "User Name attribute name",
      help: "The User Name attribute name.",
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpEmailAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Email ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: "Email attribute name",
      help: "The Email attribute name.",
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpFirstNameAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, "First Name ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: "First Name attribute name",
      help: "The First Name attribute name.",
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpLastNameAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Last Name ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: "Last Name attribute name",
      help: "The Last Name attribute name.",
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "idpFullNameAttribute",
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Full Name ", /* @__PURE__ */ React.createElement(Opt, null)),
      placeholder: "Full Name attribute name",
      help: "The Full Name attribute name.",
      disabled: isSubmitting
    }
  ), /* @__PURE__ */ React.createElement(FormSection, { title: "Defaults Configuration" }), /* @__PURE__ */ React.createElement(Field, { name: "defaultRoles", validate: validateField({ required: true }) }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      bsStyle: error ? "error" : void 0,
      help: /* @__PURE__ */ React.createElement("span", null, "The default roles synchronized user will obtain. All users need the ", /* @__PURE__ */ React.createElement("code", null, "Reader"), " role, to use the web interface"),
      error,
      id: "default-roles-select",
      label: "Default Roles",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        multi: true,
        onBlur,
        onChange: (selectedRoles) => onChange({ target: { value: selectedRoles, name } }),
        options: rolesOptions,
        placeholder: "Search for roles",
        value
      }
    )
  )), /* @__PURE__ */ React.createElement(Field, { name: "defaultUserTimezone" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "default-user-timezone-select",
      label: "Default User Time Zone",
      help: "Choose the default time zone for new users.",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      TimezoneSelect,
      {
        value: value || "Browser's time zone",
        name: "timezone",
        onChange: (newValue) => onChange({ target: { name, value: newValue } })
      }
    )
  )), validationErrors, submitErrors, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_IDP_CONFIG_SAVE_CLICKED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_section: "saml-idp-configuration-step",
          app_action_value: "save"
        });
      },
      type: "submit"
    },
    "Finish & Save Service"
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_NEXT_TEAM_SYNC_CLICKED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_section: "saml-idp-configuration-step",
          app_action_value: "next-step"
        });
        setActiveStep("team_sync");
      },
      type: "button"
    },
    "Next: Team Sync"
  )))), /* @__PURE__ */ React.createElement(Col, { md: 5 }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "The following Service Provider information is usually required to configure your Identity Provider."), /* @__PURE__ */ React.createElement(StyledDefList, null, /* @__PURE__ */ React.createElement("dt", null, "Service Provider Entity ID:"), /* @__PURE__ */ React.createElement("dd", null, values.spEntityId ? /* @__PURE__ */ React.createElement("code", null, values.spEntityId) : /* @__PURE__ */ React.createElement("em", null, "Please add a SP Entity ID value")), /* @__PURE__ */ React.createElement("dt", null, "Service Provider Assertion Consumer Service (ACS) URL:"), /* @__PURE__ */ React.createElement("dd", null, values.spBaseUrl ? /* @__PURE__ */ React.createElement("code", null, normalizeURI(`${values.spBaseUrl}/${SSO_SAML_ACS_PATH}`)) : /* @__PURE__ */ React.createElement("em", null, "Please add a SP Base URL value")), /* @__PURE__ */ React.createElement("dt", null, "Service Provider Single Logout Service URL:"), /* @__PURE__ */ React.createElement("dd", null, values.spBaseUrl ? /* @__PURE__ */ React.createElement("code", null, normalizeURI(`${values.spBaseUrl}/${SSO_SAML_LOGOUT_PATH}`)) : /* @__PURE__ */ React.createElement("em", null, "Please add a SP Base URL value")), /* @__PURE__ */ React.createElement("dt", null, "Service Provider Certificate:"), /* @__PURE__ */ React.createElement("dd", null, values.spCredentialCertificateInfo && /* @__PURE__ */ React.createElement(CertificateInfo, { certInfo: values.spCredentialCertificateInfo }) || /* @__PURE__ */ React.createElement("em", null, "Please generate or upload Service Provider credentials!"))), /* @__PURE__ */ React.createElement(StyledSectionWrapper, null, /* @__PURE__ */ React.createElement("hr", null), /* @__PURE__ */ React.createElement("h3", null, "Default Attribute Mappings"), Object.keys(defaultAttributeMapping).length === 0 ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, "Could't load default attribute mappings from the API.") : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "The authentication service tries to extract the following user attributes from the SAML authentication response by default."), /* @__PURE__ */ React.createElement(AttributeMapping, { title: "User Name", mappings: defaultAttributeMapping.user_name }), /* @__PURE__ */ React.createElement(AttributeMapping, { title: "Email", mappings: defaultAttributeMapping.email }), /* @__PURE__ */ React.createElement(AttributeMapping, { title: "First Name", mappings: defaultAttributeMapping.first_name }), /* @__PURE__ */ React.createElement(AttributeMapping, { title: "Last Name", mappings: defaultAttributeMapping.last_name }), /* @__PURE__ */ React.createElement(AttributeMapping, { title: "Full Name", mappings: defaultAttributeMapping.full_name })))));
};
const TeamSyncForm = ({ isSubmitting, defaultAttributeMapping, setActiveStep, licenseIsValid }) => {
  var _a;
  const { pathname } = useLocation();
  const [{ value: isTeamSyncEnabled }] = useField("teamSyncEnabled");
  const sendTelemetry = useSendTelemetry();
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 7 }, !licenseIsValid && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, "Team sync is only configurable with a valid license!"), /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(Input, { label: "Enable Team Sync", id: "team-sync-checkbox", labelClassName: "col-sm-3", wrapperClassName: "col-sm-9" }, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      label: "",
      type: "checkbox",
      wrapperClassName: "col-sm-12",
      name: "teamSyncEnabled",
      help: "Select this option if you want to import groups from the SAML response as teams.",
      disabled: isSubmitting || !licenseIsValid
    }
  )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledSectionWrapper, null, /* @__PURE__ */ React.createElement("p", null, "By default, the authentication service tries to extract the user groups using commonly used group attribute names from the SAML authentication response. You can define a custom group attribute name if the defaults don't work.")))), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "teamSyncGroupAttributeName",
      label: "Group Attribute Name",
      placeholder: "Group Attribute Name",
      help: "Add a SAML assertion attribute name that contains a list of group names.",
      disabled: isSubmitting || !licenseIsValid || !isTeamSyncEnabled
    }
  ), /* @__PURE__ */ React.createElement(Field, { name: "teamSyncAllowedGroups" }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      bsStyle: error ? "error" : void 0,
      help: /* @__PURE__ */ React.createElement("span", null, "You can restrict the groups that are allowed to log in by adding them here. If no group is added, all groups are allowed."),
      error,
      id: "allowed-groups",
      label: "Allowed Groups",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      MultiSelect,
      {
        id: "allowed-groups-select",
        value,
        formatCreateLabel: (label) => `Add group "${label}"?`,
        onBlur,
        placeholder: "Type group name",
        options: [],
        onChange: (selectedGroups) => onChange({ target: { value: selectedGroups, name } }),
        disabled: isSubmitting || !licenseIsValid || !isTeamSyncEnabled,
        allowCreate: true
      }
    )
  )), /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_PREVIOUS_IDP_CONFIG_CLICKED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_section: "saml-team-sync-step",
          app_action_value: "previous-step"
        });
        setActiveStep("idp_config");
      },
      type: "button"
    },
    "Previous: IdP Configuration"
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.SAML_TEAM_SYNC_SAVE_CLICKED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_section: "saml-team-sync-step",
          app_action_value: "save"
        });
      },
      type: "submit"
    },
    "Finish & Save Service"
  )))), /* @__PURE__ */ React.createElement(Col, { md: 5 }, /* @__PURE__ */ React.createElement(StyledSectionWrapper, null, /* @__PURE__ */ React.createElement("h3", null, "Default Group Attribute Mappings"), Object.keys(defaultAttributeMapping).length === 0 ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, "Could't load default group attribute mappings from the API.") : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "The authentication service tries to extract the user groups from the following attributes from the SAML authentication response by default."), /* @__PURE__ */ React.createElement("ul", null, ((_a = defaultAttributeMapping.groups) != null ? _a : []).sort().map((attributeName) => /* @__PURE__ */ React.createElement("li", { key: attributeName }, /* @__PURE__ */ React.createElement("code", null, attributeName))))))));
};
const defaultValues = {
  type: AUTH_SERVICE_TYPE,
  title: "",
  description: "",
  defaultRoles: "",
  defaultUserTimezone: void 0,
  idpMetadataUrl: "",
  idpMetadataRefreshAtLogin: false,
  idpSsoServiceRedirectBindingUrl: "",
  idpSsoServicePostBindingUrl: "",
  idpLogoutServiceRedirectBindingUrl: void 0,
  idpLogoutServicePostBindingUrl: void 0,
  idpEntityId: "",
  idpCertificate: "",
  idpCertificateFilename: "",
  idpCertificateInfo: void 0,
  idpCertificateInfoError: void 0,
  idpUserNameAttribute: void 0,
  idpEmailAttribute: void 0,
  idpFirstNameAttribute: void 0,
  idpLastNameAttribute: void 0,
  idpFullNameAttribute: void 0,
  spBaseUrl: DEFAULT_SP_BASE_URL,
  spEntityId: normalizeURI(`${DEFAULT_SP_BASE_URL}${SSO_SAML_PATH}`),
  spCredentialCertificate: "",
  spCredentialCertificateFilename: "",
  spCredentialCertificateInfo: void 0,
  spCredentialCertificateInfoError: void 0,
  spCredentialPrivateKey: { is_set: false },
  spCredentialPrivateKeyFilename: "",
  teamSyncIsCurrentlyEnabled: false,
  teamSyncEnabled: false,
  teamSyncGroupAttributeName: void 0,
  teamSyncAllowedGroups: void 0
};
const SAMLBackendConfigWizard = ({
  authBackend = defaultValues,
  initialStep = "idp_config",
  licenseIsValid
}) => {
  var _a;
  const [activeStep, setActiveStep] = useState(initialStep);
  const [submitErrors, setSubmitErrors] = useState(void 0);
  const [metadataFetchErrors, setMetadataFetchErrors] = useState(void 0);
  const [spCredentialsGenerateErrors, setSpCredentialsGenerateErrors] = useState(void 0);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const { isLoading, roles } = useRoles();
  const history = useHistory();
  const { isLoading: isDefaultAttributeMappingLoading, defaultAttributeMapping } = useSAMLDefaultAttributeMapping();
  if (isLoading || isDefaultAttributeMappingLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const onFormSubmit = (values, { setErrors, setSubmitting }) => {
    samlAuthBackendSubmit(values).then(() => {
      history.push(Routes.SYSTEM.AUTHENTICATION.BACKENDS.OVERVIEW);
    }).catch((error) => {
      var _a2, _b;
      if (typeof ((_b = (_a2 = error == null ? void 0 : error.additional) == null ? void 0 : _a2.body) == null ? void 0 : _b.errors) === "object") {
        setErrors(
          formatAuthBackendValidationErrors(error.additional.body.errors, (v) => ({
            // Add certificate errors to custom form field names to show them in the UI
            idpCertificateFilename: v.idpCertificates,
            spCredentialCertificateFilename: v.spCredentials,
            ...v
          }))
        );
      } else {
        setSubmitErrors(error);
      }
    }).finally(() => setSubmitting(false));
  };
  const handleMetadataFetch = (values, setFieldValue) => () => {
    setIsFetchingMetadata(true);
    SAMLConfig.getIdpMetadata({ url: values.idpMetadataUrl }).then((idpMetadata) => {
      var _a2;
      setFieldValue("idpSsoServiceRedirectBindingUrl", idpMetadata.sso_service_redirect_binding_url);
      setFieldValue("idpSsoServicePostBindingUrl", idpMetadata.sso_service_post_binding_url);
      setFieldValue("idpLogoutServiceRedirectBindingUrl", idpMetadata.logout_service_redirect_binding_url);
      setFieldValue("idpLogoutServicePostBindingUrl", idpMetadata.logout_service_post_binding_url);
      setFieldValue("idpEntityId", idpMetadata.entity_id);
      setFieldValue("idpCertificate", (_a2 = idpMetadata.certificates) == null ? void 0 : _a2[0]);
      setFieldValue("idpCertificateFilename", idpMetadata.certificates && "cert-from-metadata.pem");
      setMetadataFetchErrors(void 0);
    }).catch((error) => {
      var _a2, _b;
      if ((_b = (_a2 = error.additional) == null ? void 0 : _a2.body) == null ? void 0 : _b.message) {
        setMetadataFetchErrors(error.additional.body.message);
      } else {
        setMetadataFetchErrors(error.toString());
      }
    }).finally(() => setIsFetchingMetadata(false));
  };
  const handleSpCredentialFetch = (setFieldValue) => () => {
    SAMLConfig.generateSpCredentials().then((credentialResponse) => {
      setFieldValue("spCredentialCertificate", credentialResponse.certificate);
      setFieldValue("spCredentialCertificateFilename", "generated-sp-certificate.pem");
      setFieldValue("spCredentialPrivateKey", { set_value: credentialResponse.private_key });
      setFieldValue("spCredentialPrivateKeyFilename", "generated-sp-key.pem");
      setSpCredentialsGenerateErrors(void 0);
    }).catch((error) => {
      var _a2, _b;
      if ((_b = (_a2 = error.additional) == null ? void 0 : _a2.body) == null ? void 0 : _b.message) {
        setSpCredentialsGenerateErrors(error.additional.body.message);
      } else {
        setSpCredentialsGenerateErrors(error.toString());
      }
    });
  };
  const submitErrorComponent = submitErrors && /* @__PURE__ */ React.createElement(AuthBackendSubmitError, { error: submitErrors, backendId: authBackend.id });
  const defaultCreateRoleId = (_a = roles.find((role) => role.name === "Reader")) == null ? void 0 : _a.id;
  const defaultRolesValues = authBackend.defaultRoles;
  const initialValues = {
    ...authBackend,
    defaultRoles: defaultRolesValues === "" ? defaultCreateRoleId : defaultRolesValues,
    defaultUserTimezone: authBackend.defaultUserTimezone
  };
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues,
      enableReinitialize: true,
      onSubmit: onFormSubmit,
      validateOnBlur: false,
      validateOnChange: false,
      validateOnMount: false
    },
    ({ isSubmitting, values, setFieldValue, errors }) => /* @__PURE__ */ React.createElement(
      Wizard,
      {
        hidePreviousNextButtons: true,
        justified: true,
        horizontal: true,
        containerClassName: "",
        onStepChange: setActiveStep,
        activeStep,
        steps: [
          {
            key: "idp_config",
            title: /* @__PURE__ */ React.createElement("span", null, "IdP Configuration"),
            component: /* @__PURE__ */ React.createElement(
              IdPConfigForm,
              {
                values,
                roles,
                isSubmitting,
                isFetchingMetadata,
                validationErrors: /* @__PURE__ */ React.createElement(ValidationErrorSummary, { errors }),
                submitErrors: submitErrorComponent,
                metadataFetchErrors,
                spCredentialsGenerateErrors,
                setActiveStep,
                setFieldValue,
                handleMetadataFetch,
                handleSpCredentialFetch,
                defaultAttributeMapping
              }
            )
          },
          {
            key: "team_sync",
            title: /* @__PURE__ */ React.createElement("span", null, "Team Sync ", /* @__PURE__ */ React.createElement("small", null, "(Optional)")),
            component: /* @__PURE__ */ React.createElement(
              TeamSyncForm,
              {
                isSubmitting,
                defaultAttributeMapping,
                setActiveStep,
                licenseIsValid
              }
            )
          }
        ]
      }
    )
  );
};
export default SAMLBackendConfigWizard;
